/**
 * Shared eligibility-detail payload for the single-patient details screen.
 */

import { createContext, useContext, type ReactNode } from "react";
import type { EligibilityDetailResponse } from "../../data-model/eligibilityDetail";
import {
  mapEligibilityHeader,
  type EligibilityHeaderViewModel,
} from "./mapEligibilityHeader";

/** Values provided to details-view descendants. */
export interface EligibilityDetailContextValue {
  detail: EligibilityDetailResponse;
  trialId: string;
  mrn: string;
  header: EligibilityHeaderViewModel;
  /** Re-fetch eligibility detail after CRC mutations. */
  refreshDetail?: () => void;
}

const EligibilityDetailContext =
  createContext<EligibilityDetailContextValue | null>(null);

/**
 * Provide the first-paint detail payload to nested details-view sections.
 *
 * @param props.detail - Loaded `GET /detail` response
 * @param props.trialId - Trial path param
 * @param props.mrn - Patient path param
 * @param props.refreshDetail - Optional detail refetch after overrides
 * @param props.children - Details view tree
 * @returns Context provider
 */
export function EligibilityDetailProvider({
  detail,
  trialId,
  mrn,
  refreshDetail,
  children,
}: {
  detail: EligibilityDetailResponse;
  trialId: string;
  mrn: string;
  refreshDetail?: () => void;
  children: ReactNode;
}) {
  return (
    <EligibilityDetailContext.Provider
      value={{
        detail,
        trialId,
        mrn,
        header: mapEligibilityHeader(detail),
        refreshDetail,
      }}
    >
      {children}
    </EligibilityDetailContext.Provider>
  );
}

/**
 * Read the loaded eligibility detail for the current patient/trial.
 *
 * @returns Detail payload and header view model
 * @throws Error when used outside {@link EligibilityDetailProvider}
 */
export function useEligibilityDetailContext(): EligibilityDetailContextValue {
  const value = useContext(EligibilityDetailContext);
  if (!value) {
    throw new Error(
      "useEligibilityDetailContext must be used within EligibilityDetailProvider",
    );
  }
  return value;
}
